import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Briefcase, GraduationCap, Award, Mail, Sparkles, Send, X, Coins } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { formatCurrency } from "@/lib/currencyUtils";

// Public profile is anonymous — we can't call CurrencyContext (unauthenticated
// route). Salary snapshot uses the currency the profile OWNER selected, which
// the backend now includes as `salary_snapshot.currency`. Fall back to USD only
// if the backend hasn't been updated yet.
const fmt = (n, ccy) => (n ? formatCurrency(n, ccy || "USD") : "—");

export default function PublicProfile() {
  const { username } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    api.get(`/public/${username}`)
      .then(r => setP(r.data))
      .catch(() => setNotFound(true));
  }, [username]);

  if (notFound) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8" data-testid="public-not-found">
      <div className="card-flat p-10 text-center max-w-md">
        <div className="font-display font-bold text-3xl mb-3">Profile not found</div>
        <div className="text-muted-foreground mb-6">This profile is private or doesn't exist.</div>
        <Link to="/" className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-3 inline-block">Career OS home</Link>
      </div>
    </div>
  );
  if (!p) return <div className="min-h-screen bg-background flex items-center justify-center text-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-profile-page">
      <header className="glass sticky top-0 z-40 border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark size={28} className="rounded-md" />
            <span className="font-display font-bold tracking-tight">Career OS</span>
          </Link>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">public profile</div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10 grid md:grid-cols-[auto_1fr_auto] gap-8 items-start">
          <div className="w-24 h-24 bg-primary flex items-center justify-center text-foreground text-3xl font-display font-black">
            {p.name?.[0] || "?"}
          </div>
          <div>
            <div className="text-xs font-mono tracking-widest text-muted-foreground mb-2 lowercase">@{p.username}</div>
            <h1 className="hero-heading text-5xl mb-2">{p.name}</h1>
            <div className="text-lg text-foreground/80 mb-3">{p.current_role}</div>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {p.location && <div className="flex items-center gap-1.5"><MapPin size={13}/> {p.location}</div>}
              {p.years_experience && <div className="flex items-center gap-1.5"><Briefcase size={13}/> {p.years_experience}y experience</div>}
            </div>
          </div>
          <button data-testid="recruit-me-btn" onClick={()=>setShowContact(true)} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-3 flex items-center gap-2"><Send size={14}/> Recruit me</button>
        </div>

        {p.recruit_message && (
          <div className="card-flat p-6 mb-8 border-l-2 border-l-[#0F52BA]" data-testid="recruit-message">
            <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Open to opportunities</div>
            <div className="text-lg font-display font-bold">{p.recruit_message}</div>
          </div>
        )}

        {p.public_bio && (
          <div className="card-flat p-6 mb-8" data-testid="public-bio">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">About</div>
            <div className="text-zinc-200 whitespace-pre-wrap">{p.public_bio}</div>
          </div>
        )}

        {p.salary_snapshot && (
          <div className="card-flat p-6 mb-8" data-testid="public-salary">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Coins size={12}/> Compensation range</div>
            <div className="flex items-baseline gap-6">
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Current</div>
                <div className="font-display font-black text-3xl">{fmt(p.salary_snapshot.current, p.salary_snapshot.currency)}</div>
              </div>
              {p.salary_snapshot.market_median && (
                <div>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Market median</div>
                  <div className="font-display font-black text-3xl text-muted-foreground">{fmt(p.salary_snapshot.market_median, p.salary_snapshot.currency)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {p.skills?.length > 0 && (
            <div className="card-flat p-6" data-testid="public-skills">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Sparkles size={12}/> Top skills</div>
              <div className="flex flex-wrap gap-2">
                {p.skills.slice(0,20).map((s,i) => (
                  <span key={i} className="text-xs bg-card border border-border px-2 py-1">{s.name}<span className="text-muted-foreground ml-1">· {s.level}</span></span>
                ))}
              </div>
            </div>
          )}
          {p.certifications?.length > 0 && (
            <div className="card-flat p-6" data-testid="public-certs">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Award size={12}/> Certifications</div>
              <ul className="space-y-2">
                {p.certifications.map((c,i) => (
                  <li key={i} className="text-sm">
                    <div className="font-display font-bold">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.issuer}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {p.experience?.length > 0 && (
          <div className="card-flat p-6 mb-8" data-testid="public-experience">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2"><Briefcase size={12}/> Experience</div>
            <div className="space-y-4">
              {p.experience.map((e,i) => (
                <div key={i} className="border-l-2 border-primary pl-4">
                  <div className="font-display font-bold text-lg">{e.title}</div>
                  <div className="text-sm text-muted-foreground">{e.company} · {e.industry}</div>
                  <div className="text-xs font-mono text-muted-foreground mt-1">{e.start_date} — {e.end_date}</div>
                  {e.achievements && <div className="text-sm text-foreground/80 mt-2">{e.achievements}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {p.education?.length > 0 && (
          <div className="card-flat p-6 mb-8" data-testid="public-education">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><GraduationCap size={12}/> Education</div>
            <ul className="space-y-3">
              {p.education.map((e,i) => (
                <li key={i}>
                  <div className="font-display font-bold">{e.degree} {e.field && `· ${e.field}`}</div>
                  <div className="text-sm text-muted-foreground">{e.institution} · {e.start_year}—{e.end_year}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-center py-8 border-t border-border">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Built with</div>
          <Link to="/" className="font-display font-bold text-lg hover:text-primary">Career OS →</Link>
        </div>
      </div>

      {showContact && <ContactModal username={username} onClose={()=>setShowContact(false)}/>}
    </div>
  );
}

function ContactModal({ username, onClose }) {
  const [form, setForm] = useState({ from_name: "", from_email: "", from_company: "", role_offered: "", salary_range: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async () => {
    if (!form.from_name || !form.from_email || !form.message) return toast.error("Name, email, and message required");
    setSending(true);
    try { await api.post(`/public/${username}/contact`, form); setSent(true); toast.success("Message sent"); }
    catch { toast.error("Send failed"); } finally { setSending(false); }
  };
  if (sent) return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="contact-sent">
      <div className="card-flat p-10 max-w-md text-center">
        <div className="font-display font-bold text-3xl mb-3">Sent</div>
        <div className="text-muted-foreground mb-6">Your message is in their inbox. They'll reply directly to {form.from_email}.</div>
        <button onClick={onClose} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-3">Close</button>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="contact-modal">
      <div className="card-flat p-6 max-w-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-2xl">Reach out to {username}</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <F label="Your name" v={form.from_name} on={v=>setForm({...form, from_name:v})} tid="rc-name"/>
          <F label="Your email" v={form.from_email} on={v=>setForm({...form, from_email:v})} type="email" tid="rc-email"/>
          <F label="Company" v={form.from_company} on={v=>setForm({...form, from_company:v})} tid="rc-company"/>
          <F label="Role offered" v={form.role_offered} on={v=>setForm({...form, role_offered:v})} tid="rc-role"/>
          <F label="Salary range" v={form.salary_range} on={v=>setForm({...form, salary_range:v})} tid="rc-salary"/>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Message</label>
            <textarea data-testid="rc-message" value={form.message} onChange={e=>setForm({...form, message:e.target.value})} rows={4} className="w-full mt-2 bg-card border border-border px-4 py-3"/>
          </div>
        </div>
        <button data-testid="rc-send" onClick={send} disabled={sending} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors w-full mt-4 py-3">{sending? "Sending..." : "Send message"}</button>
      </div>
    </div>
  );
}
function F({ label, v, on, type = "text", tid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={tid} type={type} value={v} onChange={e=>on(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3"/>
    </div>
  );
}
